// Global entry point — runs on every page

import { initLenis, getLenis } from './motion';

// ─── Smooth Scroll (Lenis) ────────────────────────────────────────────────────
// Bridged with ScrollTrigger so pinned animations and scrubs stay in sync.
// Auto-disabled under prefers-reduced-motion or on mobile (<992px).

initLenis();

// ─── Navbar ───────────────────────────────────────────────────────────────────

function initNavbar() {
  const navbar = document.querySelector('.navbar-c');
  if (!navbar) return;

  function onScroll() {
    navbar.classList.toggle('is--scrolled', window.scrollY > 10);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

initNavbar();

// ─── Promo Banner Close ───────────────────────────────────────────────────────
// Persists "closed" state for the current browser session via sessionStorage.
// Closing the browser (or all tabs) resets the flag, so returning visitors
// see the banner again on their next visit — useful for rotating campaigns.
//
// Anti-flash: a tiny inline script in Webflow's site-wide Head Code reads the
// same key and adds .voy-promo-closed to <html> BEFORE first paint, so users
// who already closed the banner never see it flicker on subsequent pages
// within the same session.

const PROMO_STORAGE_KEY = 'voy_promo_closed';

function isPromoClosed() {
  try {
    return sessionStorage.getItem(PROMO_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function markPromoClosed() {
  try {
    sessionStorage.setItem(PROMO_STORAGE_KEY, '1');
  } catch (e) {
    // sessionStorage may be unavailable in private/locked-down browsers — non-critical
  }
}

function initPromoBanner() {
  const exitBtn = document.querySelector('.promo-banner_exit-btn');
  const wrapper = document.querySelector('.promo-banner_wrapper');
  if (!exitBtn || !wrapper) return;

  // Already closed in a prior session — head script already hid the wrapper.
  if (isPromoClosed()) return;

  exitBtn.addEventListener('click', () => {
    wrapper.style.display = 'none';
    markPromoClosed();
    // Layout shifted up — recalc all ScrollTrigger positions so scroll-driven
    // animations stay aligned with the new content offsets.
    if (typeof ScrollTrigger !== 'undefined') {
      ScrollTrigger.refresh();
    }
  });
}

initPromoBanner();

// ─── Finsweet "Load More" scroll fix (site-wide) ──────────────────────────────
// Any page that uses Finsweet v2 CMS lists with load-more will grow taller
// after each click. Lenis caches the page's scroll limit at init, so without
// re-measuring, the area below the list becomes unreachable on desktop.
// ScrollTrigger end values pegged to old page height also drift.
//
// We watch every [fs-list-element="list"] for DOM mutations (covers append,
// replace, and any future Finsweet rendering strategy) and on any childList
// change call lenis.resize() + ScrollTrigger.refresh(), debounced via rAF
// so a burst of inserts triggers a single recompute.
//
// Why not the Finsweet hook API: v2 hook names aren't reliably documented
// and the `addHook` approach was a silent no-op in the field. DOM-level
// observation is version-agnostic and survives API changes.

function initFinsweetScrollRefresh() {
  const lists = document.querySelectorAll('[fs-list-element="list"]');
  if (!lists.length) return;

  let pending = false;

  const recompute = () => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      const lenis = getLenis();
      if (lenis && typeof lenis.resize === 'function') lenis.resize();
      if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
    });
  };

  const observer = new MutationObserver((mutations) => {
    const hadChildChange = mutations.some(
      (m) => m.type === 'childList' && (m.addedNodes.length || m.removedNodes.length)
    );
    if (hadChildChange) recompute();
  });

  lists.forEach((list) => observer.observe(list, { childList: true, subtree: true }));

  // Initial pass — covers the case where the first render itself grew the
  // page past Lenis's pre-render measurement.
  requestAnimationFrame(recompute);
}

initFinsweetScrollRefresh();

// ─── Dynamic Current Year ─────────────────────────────────────────────────────

function initDynamicCurrentYear() {
  const currentYear = new Date().getFullYear();
  const elements = document.querySelectorAll('[data-current-year]');
  elements.forEach((el) => {
    el.textContent = currentYear;
  });
}

initDynamicCurrentYear();

// ─── UTM Capture ──────────────────────────────────────────────────────────────
// Capture UTM params from the URL into localStorage, then populate any
// HubSpot form's UTM inputs from that storage on every page.
// Behavior: if the URL has UTMs, we overwrite storage. That's it — no
// first-touch/last-touch distinction.

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id'];

const UTM_STORAGE_KEY = 'voy_utm_params';

function readUtmsFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const out = {};
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) out[k] = v;
  });
  return Object.keys(out).length ? out : null;
}

function saveUtms(data) {
  try {
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('[utm-capture] storage write failed', e);
  }
}

function readUtms() {
  try {
    const raw = localStorage.getItem(UTM_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Overwrite storage whenever the URL has UTMs. No lock, no expiry.
function captureUtms() {
  const urlUtms = readUtmsFromUrl();
  if (urlUtms) saveUtms(urlUtms);
}

// Read UTMs from storage and fill matching inputs on any HubSpot form.
// Retry loop + submit-time re-apply defeats HubSpot's async known-visitor
// pre-fill, which otherwise overwrites our values after onFormReady.

function applyValuesToForm(form, values) {
  UTM_KEYS.forEach((key) => {
    if (!values[key]) return;
    const input = form.querySelector(`input[name="${key}"]`);
    if (input && input.value !== values[key]) {
      input.value = values[key];
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
}

function populateUtms() {
  window.addEventListener('message', (event) => {
    if (event.data?.type !== 'hsFormCallback') return;
    if (event.data.eventName !== 'onFormReady') return;

    const values = readUtms();
    if (!values) return;

    const applyAll = () => {
      document.querySelectorAll('form.hs-form').forEach((form) => {
        applyValuesToForm(form, values);
      });
    };

    // Apply immediately, then retry every 200ms for ~2s to outlast
    // HubSpot's known-visitor pre-fill.
    requestAnimationFrame(applyAll);
    let attempts = 0;
    const interval = setInterval(() => {
      applyAll();
      if (++attempts >= 10) clearInterval(interval);
    }, 200);

    // Final re-apply right before submission (capture phase runs before
    // HubSpot's own submit handler reads the field values).
    document.querySelectorAll('form.hs-form').forEach((form) => {
      const submitBtn = form.querySelector('input[type="submit"], button[type="submit"]');
      if (submitBtn && !submitBtn.dataset.voyUtmPatched) {
        submitBtn.dataset.voyUtmPatched = '1';
        submitBtn.addEventListener('click', () => applyValuesToForm(form, readUtms() || {}), true);
      }
    });
  });
}

captureUtms();
populateUtms();
